import { createSlice } from "@reduxjs/toolkit";
import type { TemplateDTO } from "../../models/template.model";
import {
  getTemplatesByEventType,
  getTemplateById,
  saveTemplate,
} from "../thunks/templateThunks";

export interface TemplateState {
  // Get Templates By Event Type
  templatesLoading: boolean;
  templatesError: string | null;
  templates: TemplateDTO[];

  // Get Template By Id
  templateByIdLoading: boolean;
  templateByIdError: string | null;
  templateJson: string | null;

  // Save Template
  saveLoading: boolean;
  saveError: string | null;
  savedTemplateId: number | null;
  saveSuccess: boolean;
}

const initialState: TemplateState = {
  templatesLoading: false,
  templatesError: null,
  templates: [],

  templateByIdLoading: false,
  templateByIdError: null,
  templateJson: null,

  saveLoading: false,
  saveError: null,
  savedTemplateId: null,
  saveSuccess: false,
};

const templateSlice = createSlice({
  name: "template",
  initialState,
  reducers: {
    clearTemplatesError: (state) => {
      state.templatesError = null;
    },
    clearTemplateById: (state) => {
      state.templateJson = null;
      state.templateByIdError = null;
    },
    clearSaveState: (state) => {
      state.saveLoading = false;
      state.saveError = null;
      state.saveSuccess = false;
      state.savedTemplateId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Templates By Event Type
      .addCase(getTemplatesByEventType.pending, (state) => {
        state.templatesLoading = true;
        state.templatesError = null;
      })
      .addCase(getTemplatesByEventType.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.templates = action.payload.templates;
      })
      .addCase(getTemplatesByEventType.rejected, (state, action) => {
        state.templatesLoading = false;
        state.templatesError =
          action.payload || action.error.message || "Failed to fetch templates";
      })
      // Get Template By Id
      .addCase(getTemplateById.pending, (state) => {
        state.templateByIdLoading = true;
        state.templateByIdError = null;
        state.templateJson = null;
      })
      .addCase(getTemplateById.fulfilled, (state, action) => {
        state.templateByIdLoading = false;
        state.templateJson = action.payload.templateJson;
      })
      .addCase(getTemplateById.rejected, (state, action) => {
        state.templateByIdLoading = false;
        state.templateByIdError =
          action.payload || action.error.message || "Failed to fetch template";
      })
      // Save Template
      .addCase(saveTemplate.pending, (state) => {
        state.saveLoading = true;
        state.saveError = null;
        state.saveSuccess = false;
      })
      .addCase(saveTemplate.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.saveSuccess = true;
        state.savedTemplateId = action.payload.templateId;
      })
      .addCase(saveTemplate.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveError =
          action.payload || action.error.message || "Failed to save template";
      });
  },
});

export const { clearTemplatesError, clearTemplateById, clearSaveState } =
  templateSlice.actions;
export default templateSlice.reducer;
